import { z } from 'zod';
import { PartnerCompanyType } from '../partnerCompanyType.js';
/**
 * What the commercial register holds for a UID. Mirrors the projection the Zefix client returns
 * (`clients/zefix/output-models/zefixCompany.ts`); the two are kept in step by the typed assignment where a lookup is
 * turned into a verdict, so a drift is a compile error.
 */
// Zefix keys its translated strings by bare language code, not the `de-CH` form the rest of the codebase uses.
export const zefixTranslatedTextSchema = z.object({
    de: z.string().optional(),
    fr: z.string().optional(),
    it: z.string().optional(),
    en: z.string().optional(),
});
export const zefixCompanyStatusSchema = z.enum(['ACTIVE', 'CANCELLED', 'BEING_CANCELLED']);
export const ZefixCompanyStatus = zefixCompanyStatusSchema.enum;
export const zefixLegalFormSchema = z.object({
    id: z.number().optional(),
    // The eCH-0097 legal form code (`0106` is an Aktiengesellschaft), not a company UID despite the name.
    uid: z.string().optional(),
    name: zefixTranslatedTextSchema.optional(),
    shortName: zefixTranslatedTextSchema.optional(),
});
export const zefixAddressSchema = z.object({
    organisation: z.string().optional(),
    careOf: z.string().optional(),
    street: z.string().optional(),
    houseNumber: z.string().optional(),
    addon: z.string().optional(),
    poBox: z.string().optional(),
    city: z.string().optional(),
    swissZipCode: z.string().optional(),
});
export const zefixCompanySnapshotSchema = z.object({
    name: z.string(),
    uid: z.string(),
    status: zefixCompanyStatusSchema.optional(),
    legalForm: zefixLegalFormSchema.optional(),
    address: zefixAddressSchema.optional(),
    zefixDetailWeb: zefixTranslatedTextSchema.optional(),
});
/**
 * `RegisterUnavailable` is what keeps an outage from reading as a rejected UID: the register could not be asked, which
 * is not the same answer as it having no such company.
 */
export const uidVerificationOutcomeSchema = z.enum(['Found', 'NotFound', 'RegisterUnavailable']);
export const UidVerificationOutcome = uidVerificationOutcomeSchema.enum;
export const validationUidVerificationSchema = z.object({
    // The UID this verdict was reached for, so an edit cannot inherit the previous one's answer.
    uid: z.string(),
    outcome: uidVerificationOutcomeSchema,
    checkedAt: z.date(),
    company: zefixCompanySnapshotSchema.nullable(),
});
// A verdict describes one UID. Anything else on the ticket means the register has not been asked about what it holds now.
export const isUidVerificationFor = (companyUid, verification) => companyUid !== null && verification !== null && verification.uid === companyUid;
// Registered but no longer trading: the UID exists, so it is not invalid, but it is not something to approve either.
export const isZefixCompanyClosing = (company) => company?.status === ZefixCompanyStatus.CANCELLED || company?.status === ZefixCompanyStatus.BEING_CANCELLED;
export const uidComparisonResultSchema = z.enum(['Matches', 'Differs', 'NotComparable']);
export const UidComparisonResult = uidComparisonResultSchema.enum;
// Which record consolidation left the ticket pointing at, and therefore what the register was compared against.
export const uidComparisonSubjectSchema = z.enum(['Partner', 'OnboardingApplication']);
export const UidComparisonSubject = uidComparisonSubjectSchema.enum;
const uidComparedFieldSchema = z.object({
    result: uidComparisonResultSchema,
    register: z.string().nullable(),
    ours: z.string().nullable(),
});
export const validationUidComparisonSchema = z.object({
    comparedWith: uidComparisonSubjectSchema,
    companyName: uidComparedFieldSchema,
    address: uidComparedFieldSchema,
});
// Worth a warning: the register holds something for this company that contradicts what Houzy has on file.
export const hasUidComparisonDifference = (comparison) => comparison !== null &&
    [comparison.companyName, comparison.address].some((field) => field.result === UidComparisonResult.Differs);
/**
 * eCH-0097 legal form codes for the company types Houzy records. Keyed on the code rather than `legalForm.id`, which
 * is Zefix-internal, or the names, which are localized. Everything the register knows and Houzy does not — a branch
 * (`0151`), a cooperative, an association — is deliberately absent: an unmapped form is no answer, not a wrong one.
 */
export const PARTNER_COMPANY_TYPE_BY_LEGAL_FORM_CODE = {
    '0101': PartnerCompanyType.Einzelfirma,
    '0103': PartnerCompanyType.KlG,
    '0106': PartnerCompanyType.AG,
    '0107': PartnerCompanyType.GmbH,
};
export const toPartnerCompanyType = (legalForm) => {
    const code = legalForm?.uid?.trim();
    return code === undefined ? null : (PARTNER_COMPANY_TYPE_BY_LEGAL_FORM_CODE[code] ?? null);
};
